import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { Store } from '../../../types/Store';
import { StoreAutocomplete } from '../../../components/Common';
import { CommunicationSearchFilters as Filters, SpecificStatus } from '../../../hooks/Marketing';
import { ACTION_STATUS_OPTIONS, CHANNEL_OPTIONS, SPECIFIC_STATUS_OPTIONS } from './constants';

const GROUP_LABEL_SX = { mb: 1, fontWeight: 600, color: 'text.secondary' } as const;

interface FilterGroupProps {
    label: string;
    children: React.ReactNode;
}

const FilterGroup = ({ label, children }: FilterGroupProps) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={GROUP_LABEL_SX}>{label}</Typography>
        {children}
    </Box>
);

interface StatusColumnProps<T extends string | number> {
    title: string;
    options: Array<{ value: T; label: string }>;
    selected: T[];
    onChange: (values: T[]) => void;
}

function StatusColumn<T extends string | number>({ title, options, selected, onChange }: StatusColumnProps<T>) {
    const allSelected = options.length > 0 && options.every((option) => selected.includes(option.value));

    const toggleAll = () => {
        onChange(allSelected ? [] : options.map((option) => option.value));
    };

    const toggleOne = (value: T) => {
        onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    };

    return (
        <Box>
            <FormControlLabel
                control={<Checkbox size="small" checked={allSelected} onChange={toggleAll} />}
                label={<Typography variant="body2" fontWeight={600}>{title}</Typography>}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 3.5 }}>
                {options.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        control={
                            <Checkbox
                                size="small"
                                checked={selected.includes(option.value)}
                                onChange={() => toggleOne(option.value)}
                            />
                        }
                        label={<Typography variant="body2">{option.label}</Typography>}
                    />
                ))}
            </Box>
        </Box>
    );
}

interface CommunicationSearchFiltersProps {
    stores: Store[] | null;
    storesLoading: boolean;
    filters: Filters;
    onChange: (updater: (prev: Filters) => Filters) => void;
    onSearch: () => void;
    onReset: () => void;
}

const CommunicationSearchFilters = ({
    stores,
    storesLoading,
    filters,
    onChange,
    onSearch,
    onReset,
}: CommunicationSearchFiltersProps) => {
    const handleChannelChange = (_event: React.MouseEvent<HTMLElement>, newValues: string[]) => {
        onChange((prev) => ({
            ...prev,
            defChannelIds: newValues.map((value) => (value === 'null' ? null : Number(value))),
        }));
    };

    const channelValues = filters.defChannelIds.map((id) => (id === null ? 'null' : String(id)));

    return (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
                Sélection des critères
            </Typography>

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterGroup label="Magasin">
                        <FormControlLabel
                            sx={{ mb: 0.5 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={filters.allStores}
                                    onChange={(e) => onChange((prev) => ({ ...prev, allStores: e.target.checked }))}
                                />
                            }
                            label={<Typography variant="body2">Tous les magasins</Typography>}
                        />
                        <StoreAutocomplete
                            stores={stores}
                            loading={storesLoading}
                            value={filters.entityId ?? ''}
                            onChange={(storeId) => onChange((prev) => ({ ...prev, entityId: storeId }))}
                            label="Magasin"
                            disabled={filters.allStores}
                        />
                    </FilterGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FilterGroup label="Communication">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nom de la communication"
                                value={filters.defTitle}
                                onChange={(e) => onChange((prev) => ({ ...prev, defTitle: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Id de la communication"
                                value={filters.communicationInputId}
                                onChange={(e) => onChange((prev) => ({ ...prev, communicationInputId: e.target.value }))}
                            />
                        </Box>
                    </FilterGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FilterGroup label="Canal">
                        <ToggleButtonGroup
                            value={channelValues}
                            onChange={handleChannelChange}
                            sx={{
                                flexWrap: 'wrap',
                                gap: 1,
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '8px !important',
                                    mr: 0,
                                },
                            }}
                        >
                            {CHANNEL_OPTIONS.map((option) => {
                                const IconComponent = option.icon;
                                return (
                                    <ToggleButton
                                        key={option.label}
                                        value={option.id === null ? 'null' : String(option.id)}
                                        sx={{ flexDirection: 'column', gap: 0.5, textTransform: 'none', px: 1.5, py: 1, minWidth: 72 }}
                                    >
                                        <IconComponent fontSize="small" />
                                        <Typography variant="caption">{option.label}</Typography>
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </FilterGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FilterGroup label="Date de création">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Du"
                                InputLabelProps={{ shrink: true }}
                                value={filters.creationDateFrom}
                                onChange={(e) => onChange((prev) => ({ ...prev, creationDateFrom: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Au"
                                InputLabelProps={{ shrink: true }}
                                value={filters.creationDateTo}
                                onChange={(e) => onChange((prev) => ({ ...prev, creationDateTo: e.target.value }))}
                            />
                        </Box>
                    </FilterGroup>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Statuts
            </Typography>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <StatusColumn<SpecificStatus>
                        title="Brouillons"
                        options={SPECIFIC_STATUS_OPTIONS}
                        selected={filters.specificStatuses}
                        onChange={(values) => onChange((prev) => ({ ...prev, specificStatuses: values }))}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <StatusColumn<number>
                        title="Communications extraites"
                        options={ACTION_STATUS_OPTIONS}
                        selected={filters.actionStatusIds}
                        onChange={(values) => onChange((prev) => ({ ...prev, actionStatusIds: values }))}
                    />
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onReset}>
                    Réinitialiser
                </Button>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={onSearch}>
                    Rechercher
                </Button>
            </Box>
        </Paper>
    );
};

export default CommunicationSearchFilters;
